import { G } from '@mobily/ts-belt';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { FC, ReactElement, useCallback, useState } from 'react';
import {
  BooleanField,
  TextField,
  useGetIdentity,
  useGetOne,
  useRecordContext,
  useTranslate,
  WithRecord,
} from 'react-admin';

import { adminGroupName } from '@Common/types/bo/authorization';
import {
  GetB2bAdminsShowResponse,
  PostB2bAdminsRequestOtpEndpoint,
} from '@Common/types/bo/b2b/admins';
import { B2bCompanyDocumentDetails } from '@Common/types/bo/b2b/companies';

import {
  adminRoleChoices,
  adminStatusChoices,
  billingChannelChoices,
  languagePreferenceChoices,
} from '@sh4res/bo-common/choices/admins';
import {
  Button,
  DialogTitleWithX,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
} from '@sh4res/bo-common/components';
import { B2bAdminEdit } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminEdit';
import { B2bAdminSendInvitesButton } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminSendInvitesButton';
import { canResendB2bAdminInvitation } from '@sh4res/bo-common/components/B2bAdmins/canResendB2bAdminInvitation';
import { isThirdPartyLevel3 } from '@sh4res/bo-common/components/B2bAdmins/ThirdPartyMandateFields';
import { genderChoices } from '@sh4res/bo-common/components/B2bEmployee/choices';
import { LastOtpField } from '@sh4res/bo-common/components/B2bLastOtpField';
import {
  EmailStateChip,
  IdField,
  ReferenceSingleField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { MaskedEmailField } from '@sh4res/bo-common/fields/MaskedEmailField';
import { useCanAccess, useCreatePromise, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { formatFullname } from '@sh4res/bo-common/utils';
import { isB2bAdmin, isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bAdminShow';

export const B2bAdminShow: FC<{ ghostLinkComponent: ReactElement }> = ({ ghostLinkComponent }) => {
  const { data: identity } = useGetIdentity();
  const resourceBasePath = useResourceBasePath();
  const canAccessEdit = useCanAccess({
    resource: `${resourceBasePath}admins`,
    action: 'edit',
  });
  const canAccessGhostLogin = useCanAccess({
    resource: `${resourceBasePath}admins/login_link`,
    action: 'show',
  });
  const isRestrictedModification = useCanAccess({
    resource: `${resourceBasePath}admins/restricted_modification`,
    action: 'edit',
  });

  const isSuperAdmin =
    !isBoExternalIdentity(identity) && (identity?.roles.includes(adminGroupName) ?? false);
  return (
    <ShowWithNavigation
      avatar={{ name_source: 'firstname' }}
      title={
        <WithRecord
          render={(record: GetB2bAdminsShowResponse) =>
            record.firstname ? formatFullname(record) : record.email
          }
        />
      }
      titleActions={<Actions />}
      actions={false}
      button={
        <WithRecord
          render={(record: GetB2bAdminsShowResponse) => {
            const isBoInternalWithEditPermission = !isBoExternal() && canAccessEdit;
            const isBoExternalSuperAdmin =
              isBoExternal() &&
              isBoExternalIdentity(identity) &&
              identity.roles.includes('admin_level_4') &&
              record.role !== 'admin_level_4';
            const isConnectedAdminAccount = isB2bAdmin(identity) && identity.id === record.id;

            return isBoInternalWithEditPermission ||
              isBoExternalSuperAdmin ||
              isConnectedAdminAccount ||
              isRestrictedModification ? (
              <B2bAdminEdit isRestrictedModification={isRestrictedModification && !isSuperAdmin} />
            ) : undefined;
          }}
        />
      }
      logs={isBoExternal() ? undefined : { resource: 'b2b_admin' }}>
      <ShowSection label="general.sections.details">
        <ShowFields<
          GetB2bAdminsShowResponse & { ghost_login: ReactElement; company_name: ReactElement }
        >
          labels={{
            has_last_otp: 'resources.bo_common.b2b.admins.fields.has_last_otp',
          }}
          fields={{
            id: (
              <IdField data-testid={`${testId}_IdField`} reference={`${resourceBasePath}admins`} />
            ),
            company_id: (
              <IdField
                data-testid={`${testId}_CompanyIdField`}
                reference={`${resourceBasePath}companies`}
              />
            ),
            company_name: (
              <>
                <ReferenceSingleField
                  data-testid={`${testId}_CompanyNameField`}
                  reference={`${resourceBasePath}companies`}
                  source="company_id">
                  <TextField data-testid={`${testId}_CompanyNameTextField`} source="name" />
                </ReferenceSingleField>
              </>
            ),
            role_in_company: (
              <TextField
                data-testid={`${testId}_RoleInCompanyField`}
                label="resources.bo_common.b2b.admins.fields.role_in_company"
              />
            ),
            email: (
              <WithRecord
                render={(record: GetB2bAdminsShowResponse) => (
                  <Stack direction="row" spacing={2} alignItems={'center'}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      sx={{ columnGap: 1, alignItems: 'center' }}>
                      <TextField data-testid={`${testId}_EmailField`} source="email" />
                      {record.email_verified && <VerifiedIcon fontSize="small" />}
                    </Box>
                    <EmailStateChip
                      source="email_state"
                      emailSource="email"
                      logResource="b2b_admin"
                    />
                  </Stack>
                )}
              />
            ),
            status: (
              <SelectChipField
                data-testid={`${testId}_StatusField`}
                choices={adminStatusChoices}
                label="resources.bo_common.b2b.admins.fields.status"
              />
            ),
            role: (
              <SelectChipField
                data-testid={`${testId}_RoleField`}
                choices={adminRoleChoices}
                label="resources.bo_common.b2b.admins.fields.role"
              />
            ),
            gender: (
              <SelectChipField data-testid={`${testId}_GenderField`} choices={genderChoices} />
            ),
            firstname: <TextField data-testid={`${testId}_FirstnameField`} />,
            lastname: <TextField data-testid={`${testId}_LastnameField`} />,
            phone_number: <TextField data-testid={`${testId}_PhoneNumberField`} />,
            ...(!isBoExternal() && {
              secondary_email: (
                <Stack direction="row" spacing={2} alignItems={'center'}>
                  <MaskedEmailField
                    source="secondary_email"
                    testId={`${testId}_SecondaryEmailField`}
                  />
                  <EmailStateChip
                    source="secondary_email_state"
                    emailSource="secondary_email"
                    logResource="b2b_admin"
                  />
                </Stack>
              ),
            }),
            ...(!isBoExternal() && {
              has_last_otp: (
                <LastOtpField
                  data-testid={`${testId}_LastOtpField`}
                  // eslint-disable-next-line regex/invalid -- only in BO
                  resourcePath="b2b/admins/last_otp"
                  labels={{
                    show: 'resources.bo_common.b2b.admins.actions.last_otp.show',
                    hide: 'resources.bo_common.b2b.admins.actions.last_otp.hide',
                    noOtp: 'resources.bo_common.b2b.admins.actions.last_otp.no_last_otp',
                    unavailable:
                      'resources.bo_common.b2b.admins.notifications.last_otp_unavailable',
                    fetchError: 'resources.bo_common.b2b.admins.notifications.last_otp_fetch_error',
                  }}
                />
              ),
            }),
            language_preference: (
              <SelectChipField
                data-testid={`${testId}_LanguagePreferenceField`}
                choices={languagePreferenceChoices}
              />
            ),
            is_third_party: <BooleanField data-testid={`${testId}_IsThirdPartyField`} />,
            created_at: (
              <UTCDateField data-testid={`${testId}_CreatedAtField`} showTime={!isBoExternal()} />
            ),
            updated_at: (
              <UTCDateField data-testid={`${testId}_UpdatedAtField`} showTime={!isBoExternal()} />
            ),
            last_invitation_sent_at: (
              <UTCDateField data-testid={`${testId}_LastInvitationSentAtField`} showTime />
            ),
            email_verified: <BooleanField data-testid={`${testId}_EmailVerifiedField`} />,
            ...(canAccessGhostLogin && { ghost_login: ghostLinkComponent }),
            ...(!isBoExternal() && {
              is_migrated: <BooleanField data-testid={`${testId}_IsMigratedField`} />,
            }),
          }}
        />
      </ShowSection>
      <CorrespondenceSection kind="mailing" />
      <CorrespondenceSection kind="billing" />
      <ThirdPartyMandateSection />
    </ShowWithNavigation>
  );
};

type CorrespondenceKind = 'mailing' | 'billing';

// Mailing / billing correspondence: an empty address means the company address is used
const CorrespondenceSection = ({ kind }: { kind: CorrespondenceKind }) => {
  const record = useRecordContext<GetB2bAdminsShowResponse>();
  const translate = useTranslate();

  if (!record) {
    return null;
  }

  const capitalizedKind = kind === 'mailing' ? 'Mailing' : 'Billing';
  // Static `data-testid` suffixes are enforced on the JSX attributes: the kind is resolved here
  const id = (suffix: string) => `${testId}_${suffix}`;
  const isCorrespondent = record[`is_${kind}_correspondent`];
  const hasAddress = [
    record[`${kind}_street`],
    record[`${kind}_postalcode`],
    record[`${kind}_city`],
    record[`${kind}_country`],
  ].some(G.isNotNullable);

  const fields: Partial<Record<keyof GetB2bAdminsShowResponse, ReactElement>> = {
    [`is_${kind}_correspondent`]: (
      <BooleanField data-testid={id(`Is${capitalizedKind}CorrespondentField`)} />
    ),
    ...(isCorrespondent && {
      ...(kind === 'billing' && {
        billing_channel: (
          <SelectChipField
            data-testid={`${testId}_BillingChannelField`}
            choices={billingChannelChoices}
          />
        ),
      }),
      ...(hasAddress
        ? {
            [`${kind}_street`]: <TextField data-testid={id(`${capitalizedKind}StreetField`)} />,
            [`${kind}_postalcode`]: (
              <TextField data-testid={id(`${capitalizedKind}PostalcodeField`)} />
            ),
            [`${kind}_city`]: <TextField data-testid={id(`${capitalizedKind}CityField`)} />,
            [`${kind}_country`]: <TextField data-testid={id(`${capitalizedKind}CountryField`)} />,
            [`is_npai_${kind}`]: <BooleanField data-testid={id(`IsNpai${capitalizedKind}Field`)} />,
            ...(record[`is_npai_${kind}`] && {
              [`npai_${kind}_at`]: (
                <UTCDateField data-testid={id(`Npai${capitalizedKind}AtField`)} />
              ),
            }),
          }
        : {
            [`${kind}_street`]: (
              <Typography variant="body2" data-testid={id(`${capitalizedKind}CompanyAddressField`)}>
                {translate('resources.bo_common.b2b.admins.correspondence.company_address_used')}
              </Typography>
            ),
          }),
    }),
  };

  return (
    <ShowSection label={`resources.bo_common.b2b.admins.sections.${kind}_address`}>
      <ShowFields<GetB2bAdminsShowResponse> fields={fields} />
    </ShowSection>
  );
};

// Third-party legal structure and mandate document are internal-BO only and exist only
// for level-3 third-party correspondents.
const ThirdPartyMandateSection = () => {
  const record = useRecordContext<GetB2bAdminsShowResponse>();
  const resourceBasePath = useResourceBasePath();

  const { data: mandateDocument } = useGetOne<B2bCompanyDocumentDetails>(
    `${resourceBasePath}companies/documents`,
    { id: record?.third_party_mandate_document_id ?? 0 },
    { enabled: G.isNotNullable(record?.third_party_mandate_document_id) },
  );

  if (
    isBoExternal() ||
    !record ||
    !isThirdPartyLevel3({ is_third_party: record.is_third_party, role: record.role })
  ) {
    return null;
  }

  return (
    <ShowSection label="resources.bo_common.b2b.admins.sections.third_party">
      <ShowFields<GetB2bAdminsShowResponse>
        fields={{
          third_party_legal_structure: (
            <TextField data-testid={`${testId}_ThirdPartyLegalStructureField`} />
          ),
          third_party_mandate_document_id: (
            <DocumentListViewerLink<B2bCompanyDocumentDetails>
              urlSource="document_url"
              nameSource="document_name"
              record={mandateDocument}
              testId={`${testId}_ThirdPartyMandateDocument`}
            />
          ),
        }}
      />
    </ShowSection>
  );
};

const Actions = () => {
  const record = useRecordContext<GetB2bAdminsShowResponse>();

  if (!record) {
    return null;
  }

  return (
    <>
      {!isBoExternal() && <RequestOtpSection />}
      <B2bAdminSendInvitesButton
        adminIds={[record.id]}
        companyId={record.company_id}
        disabled={!canResendB2bAdminInvitation(record)}
        testId="BoExternalB2bAdminShow_SendInviteButton"
      />
    </>
  );
};

const RequestOtpSection = () => {
  const record = useRecordContext<GetB2bAdminsShowResponse>();
  const translate = useTranslate();
  const [code, setCode] = useState<string | null>(null);
  const [actionReviewRequest, { isLoading }] = useCreatePromise<
    PostB2bAdminsRequestOtpEndpoint['Client']['RequestBody'],
    PostB2bAdminsRequestOtpEndpoint['Client']['ResponseBody']
  >();

  const handleRequestCode = useCallback(async () => {
    if (isLoading || !record) {
      return;
    }

    const res = await actionReviewRequest('b2b/admins/request_otp', {
      data: {
        admin_id: record.id,
      },
    });

    setCode(res.code);
  }, [isLoading, record, actionReviewRequest]);

  const handleClose = useCallback(() => {
    setCode(null);
  }, []);

  const hidden = record?.phone_number;

  if (hidden) {
    return null;
  }

  return (
    <>
      <Button
        permissions={{ resource: 'edit_requests', action: 'edit' }}
        label="resources.bo_common.b2b.admins.actions.request_otp.label"
        sx={{
          height: '30px',
        }}
        data-testid="BoExternalB2bAdminShow_RequestOtpButton"
        size="small"
        onClick={handleRequestCode}></Button>
      <Dialog open={!!code} onClose={handleClose}>
        <DialogTitleWithX onClose={handleClose}>
          {translate('resources.bo_common.b2b.admins.actions.request_otp.dialog.title')}
        </DialogTitleWithX>
        <DialogContent>
          <Typography variant="body2">
            {translate('resources.bo_common.b2b.admins.actions.request_otp.dialog.description')}
          </Typography>

          <Typography sx={{ textAlign: 'center', py: 2 }} variant="body1">
            {code}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};
